import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import invariant from 'tiny-invariant'

import {
  ClickableList,
  ElasticContentContainer,
  SortingIconButton
} from '@cdab/scania/qpr/components/atoms'
import type { Dealer } from '@cdab/scania/qpr/schema'
import { TdsDropdown, TdsDropdownOption } from '@scania/tegel-react'

const HeaderWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  margin: 16px 16px 0;
`

const StyledTdsDropdown = styled(TdsDropdown)`
  width: 100%;
`
export type SortOption = {
  sortFunction: (a: Dealer, b: Dealer) => number
  translationKey: string
  id: string
}

export type DashboardWidgetListProps = {
  emptyMessage: React.ReactNode
  children?: React.ReactNode[]
  className?: string
  sortOptions: SortOption[]
  defaultSortOptionID?: SortOption['id']
  onSelectSorting: (option: SortOption) => void
}

export function ExpiringCertificatesList(props: DashboardWidgetListProps) {
  const { t } = useTranslation('common')
  const [reverse, setReverse] = useState(false)

  const onSelectChange = useCallback(
    (selectedId: string | null) => {
      if (selectedId === null) return
      const selectedSortOption = props.sortOptions.find(
        ({ id }) => selectedId === id
      )
      invariant(
        selectedSortOption,
        `No sort option found when looking for ${selectedId}`
      )

      props.onSelectSorting(selectedSortOption)
    },
    [props]
  )

  return (
    <ElasticContentContainer
      scrollY
      header={
        <HeaderWrapper>
          <StyledTdsDropdown
            label={t('sorting')}
            size='sm'
            labelPosition='outside'
            placeholder={t('select-sort-order')}
            onTdsChange={e =>
              onSelectChange(e.target.value?.toString() ?? null)
            }
          >
            {props.sortOptions.map(sortOption => {
              return (
                <TdsDropdownOption key={sortOption.id} value={sortOption.id}>
                  {t(sortOption.translationKey)}
                </TdsDropdownOption>
              )
            })}
          </StyledTdsDropdown>
          <SortingIconButton onClick={() => setReverse(!reverse)} />
        </HeaderWrapper>
      }
    >
      <ClickableList scrollable={false} className={props.className}>
        {reverse ? props.children?.slice().reverse() : props.children}
      </ClickableList>
    </ElasticContentContainer>
  )
}
